import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="file-drop"
// Drag-and-drop file upload for documents. Fully reusable — works for any
// documentable parent by accepting configurable values.
//
// Usage:
//   <div data-controller="file-drop"
//        data-file-drop-url-value="/companies/1/documents"
//        data-file-drop-frame-id-value="documents_list"
//        data-action="dragenter->file-drop#dragEnter
//                     dragover->file-drop#dragOver
//                     dragleave->file-drop#dragLeave
//                     drop->file-drop#drop">
//     <div data-file-drop-target="overlay" class="hidden ...">
//       Drop files to upload
//     </div>
//     ...existing content...
//   </div>
export default class extends Controller {
  static targets = ["overlay", "fileInput"]

  static values = {
    url: String,
    frameId: { type: String, default: "documents_list" },
    paramName: { type: String, default: "document" },
    fileField: { type: String, default: "file" },
    documentType: { type: String, default: "unclassified" }
  }

  connect() {
    this.dragCounter = 0
  }

  dragEnter(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter++
    if (this.dragCounter === 1) {
      this.showOverlay()
    }
  }

  dragOver(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
  }

  dragLeave(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter--
    if (this.dragCounter === 0) {
      this.hideOverlay()
    }
  }

  drop(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.dragCounter = 0
    this.hideOverlay()

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.uploadFiles(files)
  }

  isFileDrag(event) {
    return event.dataTransfer?.types?.includes("Files")
  }

  browse() {
    this.fileInputTarget.click()
  }

  fileSelected() {
    const files = Array.from(this.fileInputTarget.files)
    if (files.length === 0) return

    this.uploadFiles(files)
    this.fileInputTarget.value = ""
  }

  async uploadFiles(files) {
    const total = files.length
    let completed = 0
    let failed = 0

    this.showProgress(completed, total)

    const uploads = files.map(async (file) => {
      try {
        await this.uploadFile(file)
      } catch {
        failed++
      } finally {
        completed++
        this.showProgress(completed, total)
      }
    })

    await Promise.all(uploads)
    this.hideOverlay()

    const succeeded = total - failed

    const noun = this.documentTypeValue === "photo" ? "photo" : "document"

    if (succeeded > 0) {
      this.reloadFrame()
      this.showToast("success", `${succeeded} ${noun}${succeeded === 1 ? "" : "s"} uploaded`)
    }

    if (failed > 0) {
      this.showToast("error", `${failed} ${noun}${failed === 1 ? "" : "s"} failed to upload`)
    }
  }

  async uploadFile(file) {
    const formData = new FormData()
    formData.append(`${this.paramNameValue}[${this.fileFieldValue}]`, file)
    if (this.documentTypeValue !== "photo") {
      formData.append(`${this.paramNameValue}[document_type]`, "unclassified")
    }

    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`)
    }

    return response.json()
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
  }

  hideOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
  }

  showProgress(completed, total) {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    const label = this.overlayTarget.querySelector("[data-role='label']")
    if (label) {
      label.textContent = (completed < total)
        ? `Uploading ${completed + 1} of ${total}...`
        : "Processing..."
    }
  }

  reloadFrame() {
    const frame = document.getElementById(this.frameIdValue)
    if (frame?.reload) {
      frame.reload()
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta?.content || ""
  }
}
